<template>
    <step-wrapper>
        <step-heading class="flex items-center justify-between">
            {{ $t('MONOGRAMMING') }}
            <button
                class="rounded text-[14px] font-normal uppercase underline focus-outline-only"
                @click="clearPlacement('Monogram')"
            >
                {{ $t('CLEAR') }}
            </button>
        </step-heading>

        <step-section-heading>
            {{ $t('DECORATION_LOCATION') }}
        </step-section-heading>

        <div class="mb-[24px] flex gap-[12px]">
            <primary-button
                v-for="option in placementOptions"
                :key="option.code"
                :is-active="option.selected"
                @click="selectPlacement(option)"
            >
                {{option.name}}
            </primary-button>
        </div>

        <step-section-heading>
            {{ $t('STYLE') }}
        </step-section-heading>
        <div class="mb-[24px] flex flex-wrap gap-[12px]">
            <primary-button
                v-for="{ option } in fontOptions"
                :key="option.code"
                :size-in-px="64"
                :is-active="option.code === selectedFont"
                @click="selectFont(option)"
                selected-style="outline"
                :label="viewController.componentNames(option).join('')"
            >
                <span class="absolute inset-[10px] bg-contain bg-center bg-no-repeat"
                    :style="{
                        backgroundImage: `url(&quot;${option.custom.image}&quot;)`
                    }"
                >
                </span>
            </primary-button>
        </div>

        <step-section-heading>
            {{ $t('INITIALS') }}
        </step-section-heading>
        <div class="flex gap-[12px] pl-1">
            <initial-input v-for="initial in letters"
                :key="initial.index"

                ref="initials"

                :label="initial.label"
                :placeholder="initial.placeholder"
                :value="initial.value"

                @input="updateInitial(initial, $event)"
                @back="goBack(initial)"

                @focus="openKeyboard"
                @blur="closeKeyboard"
            />
        </div>
        <div v-if="requireMessage"
            class="mt-[8px] flex text-sm text-gray-dark"
        >
            {{ requireMessage }}
        </div>
    </step-wrapper>
</template>

<script>
    import StepHeading from '../UI/headings/StepHeading.vue';
    import StepWrapper from '../wrappers/StepWrapper.vue';
    import StepSectionHeading from '../UI/headings/StepSectionHeading.vue';
    import PrimaryButton from '../UI/buttons/PrimaryButton.vue';
    import InitialInput from '../UI/inputs/InitialInput.vue';

    import allStores from '../../stores/all';

    export default {
        name: 'MonogramStep',
        mixins: [allStores],

        components: {
            StepHeading,
            StepWrapper,
            StepSectionHeading,
            PrimaryButton,
            InitialInput,
        },

        data() {
            return {
                letters: [],

                selectedFont: '',
            };
        },

        computed: {
            viewController() {
                return this.customizerStore.viewController;
            },

            monogramOptions() {
                return this.customizerStore.monogramOptions;
            },

            placementOptions() {
                const list = [];

                const placement = this.monogramOptions.find((x) => x.description.definition === 'Monogram Placement');

                if (placement == null) {
                    return list;
                }

                for (const option of placement.options) {
                    list.push({
                        name: option.option.name,
                        code: option.option.code,
                        selected: option.selected,
                    });
                }

                return list;
            },

            fontOptions() {
                return this.monogramOptions
                    .filter((option) => option.description.definition === 'Monogram Font')?.[0]?.options;
            },

            selectedOption() {
                const selected = this.viewController.selectedAtPlacement('Monogram');

                return selected;
            },

            minCharacters() {
                return +(this.selectedOption?.custom?.['personalization-limit-min'] ?? '3');
            },

            maxCharacters() {
                return +(this.selectedOption?.custom?.['personalization-limit-max'] ?? '3');
            },

            requireMessage() {
                const text = this.getText();

                if (text.length < this.minCharacters) {
                    if (this.minCharacters < 2) {
                        return this.$t('CHARACTER_IS_MANDATORY', { min: this.minCharacters });
                    }

                    return this.$t('CHARACTERS_ARE_MANDATORY', { min: this.minCharacters });
                }

                return null;
            },

            isMobile() {
                return this.appStore.isMobile;
            },

            selectedGroupItem() {
                return this.customizerStore.selectedGroupItem;
            },
        },

        watch: {
            maxCharacters(value) {
                if (value === 2) {
                    const text = this.getText();
                    // Chop off the middle letter if needed.
                    if (text.length > 2) {
                        this.parsetext(text[0] + text[2]);
                    }
                }
            },
        },

        mounted() {
            this.readMonogramFromController();

            const selectedFontCode = this.viewController.selectedAtPlacement('Monogram')?.description?.find(
                (y) => y.definition === 'Monogram Font',
            )?.code;

            this.selectedFont = this.fontOptions.find((x) => x.option.code === selectedFontCode)?.option?.code;
        },

        methods: {
            clearPlacement(placementCode) {
                this.customizerStore.clearPlacement(placementCode);

                this.selectedFont = this.fontOptions[0].option.code;

                this.readMonogramFromController();
            },

            updateInitial(initial, input) {
                initial.value = input;

                if (input.length > 0) {
                    this.selectMonogram(initial);
                } else {
                    this.selectMonogram();
                }
            },

            goBack(initial) {
                const prev = this.letters[initial.index - 1];
                if (prev) {
                    const prevControl = this.$refs.initials[prev.index];
                    if (prevControl) {
                        prevControl.focus();
                    }
                }
            },

            selectPlacement(option) {
                this.viewController.updateSimilarComponent('Monogram', 'Monogram Placement', option.code);
            },

            selectFont(option) {
                this.viewController.updateSimilarComponent('Monogram', 'Monogram Font', option.code);

                this.selectedFont = option.code;
            },

            advance(initial) {
                if (initial) {
                    const next = this.letters[initial.index + 1];
                    if (next) {
                        const nextControl = this.$refs.initials[next.index];
                        if (nextControl) {
                            nextControl.focus();
                        }
                    }
                }
            },

            async selectMonogram(initial) {
                const monogram = this.getText();

                if (monogram.length < this.minCharacters) {
                    this.advance(initial);
                    return;
                }

                const updateResult = await this.viewController.updatePersonalization('Monogram', monogram);

                if (updateResult.accepted) {
                    // Clear text since these both are mutually exclusive.
                    this.customizerStore.clearPlacement('Text');

                    this.viewController.clearPlacementTags('Monogram');

                    if (monogram.length > 0) {
                        this.viewController.addPlacementTag('Monogram', 'personalized');
                    }

                    this.viewController.updateComponents();
                    this.lastAccepted = monogram;

                    if (this.selectedGroupItem) {
                        this.customizerStore.updateGroupPersonalization(this.selectedGroupItem, monogram);
                    }

                    // Advance if accepted.
                    this.advance(initial);
                } else if (initial) {
                    initial.value = '';
                }
            },

            readMonogramFromController() {
                const currentMonogram = this.viewController.selectedAtPlacement('Monogram');

                const text = currentMonogram?.custom?.['personalization-text'] ?? '';
                this.lastAccepted = '';

                this.parseText(text);
            },

            parseText(text) {
                const labels = [
                    this.$t('FIRST_INITIAL'),
                    this.$t('MIDDLE_INITIAL'),
                    this.$t('LAST_INITIAL'),
                ];

                const placeholders = ['A', 'B', 'C'];

                this.letters = [];

                for (let i = 0; i < this.maxCharacters; i += 1) {
                    this.letters.push({
                        index: i,
                        value: text[i] ?? '',
                        label: labels[i] ?? '',
                        placeholder: placeholders[i],
                    });
                }
            },

            getText() {
                return this.letters.map((x) => x.value).join('');
            },

            openKeyboard() {
                if (this.isMobile) {
                    this.appStore.toggleKeyboard(true);
                }
            },

            closeKeyboard() {
                if (this.isMobile) {
                    this.appStore.toggleKeyboard(false);
                }
            },
        },

    };
</script>
